/**
 * HeaderScroller manages the dynamic height adjustment of a scrollable header container.
 * It listens to scroll events and updates CSS custom properties to enable smooth header shrinking animations.
 * Supports both window scrolling and custom scroll containers.
 */
export class HeaderScroller {
    /** @private Observer for content changes within the scrollable container  */
    private contentObserver: MutationObserver | null = null;

    /** @private The scrollable container element that changes height */
    private scrollableContainerElement: HTMLElement | null;

    /** @private The buffer element used for layout spacing */
    private bufferElement: HTMLElement | null;

    /** @private The scroll container to listen to (window or custom element) */
    private scrollContainer: HTMLElement | Window;

    /** @private The maximum natural height of the scrollable container */
    private maxHeight: number = 0;

    /** @private Bound scroll handler for proper event listener removal */
    private boundUpdateHeight: () => void;

    /**
     * Initializes a new instance of the HeaderScroller.
     * 
     * @param scrollContainerSelector - Optional CSS selector for a custom scroll container. If not provided, uses window.
     * 
     * @example
     * ```typescript
     * // Use window as scroll container
     * const scroller = new HeaderScroller();
     * 
     * // Use custom scroll container
     * const scroller = new HeaderScroller('.custom-scroll-container');
     * ```
     */
    constructor(scrollContainerSelector?: string) {
        this.scrollableContainerElement = document.querySelector<HTMLElement>(
            ".header-v2__scrollable-container",
        );
        
        this.bufferElement = document.querySelector<HTMLElement>(
            ".header-v2__buffer",
        );

        // Determine the scroll container
        if (scrollContainerSelector) {
            const customContainer = document.querySelector<HTMLElement>(scrollContainerSelector);
            this.scrollContainer = customContainer || window;
        } else {
            this.scrollContainer = window;
        }

        // Pre-bind event handler for proper removal
        this.boundUpdateHeight = this.UpdateHeight.bind(this);

        if (this.scrollableContainerElement == null || this.bufferElement == null) {
            return;
        }

        // Wait for layout to complete before measuring
        requestAnimationFrame(() => {
            this.Initialize();
        });
    }

    private ResetHeight(): void {
        document.body.style.removeProperty('--header-scrollable-max-height');
        document.body.style.removeProperty('--header-delta-height');

        // Capture the natural rendered height
        this.maxHeight = this.scrollableContainerElement.offsetHeight;
        this.UpdateHeight();
    }

    /**
     * Initializes the header scroller by capturing the natural height and setting up event listeners.
     * 
     * @private
     * @returns {void}
     */
    private Initialize(): void {
        if (this.scrollableContainerElement == null || this.bufferElement == null) {
            return;
        }

        this.ResetHeight();

        // Attach scroll listener to the appropriate container
        this.scrollContainer.addEventListener("scroll", this.boundUpdateHeight, { passive: true });

        // Set up a MutationObserver to watch for content changes
        this.contentObserver = new MutationObserver(() => {
            this.ResetHeight();
        });
        this.contentObserver.observe(this.scrollableContainerElement, {
            childList: true,      // Watch for added/removed children
            subtree: true,        // Watch descendants too
            attributes: true,     // Watch attribute changes
            characterData: true   // Watch text content changes
        });
    }

    /**
     * Updates the header height based on the current scroll position.
     * Sets CSS custom properties for dynamic height and delta calculations.
     * 
     * @private
     * @returns {void}
     */
    private UpdateHeight(): void {
        if (this.scrollableContainerElement == null || this.bufferElement == null) {
            return;
        }

        // Get scroll position from the appropriate container
        const scrollY = this.scrollContainer instanceof Window 
            ? window.scrollY 
            : this.scrollContainer.scrollTop;

        const newHeight = Math.max(this.maxHeight - scrollY, 0);
        const shrunkAmount = this.maxHeight - newHeight;

        document.body.style.setProperty('--header-scrollable-max-height', `${newHeight}px`);
        document.body.style.setProperty('--header-delta-height', `${shrunkAmount}px`);
    }

    /**
     * Disposes the HeaderScroller instance by removing event listeners.
     * Should be called when the component is no longer needed to prevent memory leaks.
     * 
     * @public
     * @returns {void}
     */
    public Dispose(): void {
        // Remove scroll listener using the bound reference
        this.scrollContainer.removeEventListener("scroll", this.boundUpdateHeight);
        this.contentObserver?.disconnect();
    }
}

/**
 * Global factory function for creating HeaderScroller instances.
 * 
 * @global
 * @function InitHeaderScroller
 * @param {string} [scrollContainerSelector] - Optional CSS selector for a custom scroll container
 * @returns {HeaderScroller} A new HeaderScroller instance
 * 
 * @example
 * ```javascript
 * // Called from C# via JSInterop
 * const scroller = InitHeaderScroller();
 * // Or with custom container
 * const scroller = InitHeaderScroller('.my-scroll-container');
 * ```
 */
window['InitHeaderScroller'] = function (scrollContainerSelector?: string) {
    return new HeaderScroller(scrollContainerSelector);
};